import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Award, MapPin, Beaker, TestTube, Star, CheckCircle } from 'lucide-react';

const Home: React.FC = () => {
  const premiumImages = [
    '/img/title/bromine1.jpg',
    '/img/title/bromine2.jpg',
    '/img/title/bromine3.jpg',
    '/img/title/bromine4.jpg',
    '/img/title/bromine5.jpg',
    '/img/title/bromine6.jpg',
    '/img/title/magnesium_chloride1.jpeg',
    '/img/title/magnesium_chloride2.jpg',
    '/img/title/magnesium_chloride3.jpg',
    '/img/title/magnesium_chloride4.jpg'
  ];

  const features = [
    {
      icon: <Shield className="w-8 h-8 text-chemical-600" />,
      title: 'Quality Assurance',
      description: 'Premium quality chemical products that meet international standards and specifications.'
    },
    {
      icon: <Beaker className="w-8 h-8 text-chemical-600" />,
      title: 'Technical Excellence',
      description: 'Advanced manufacturing processes with rigorous quality control at every stage.'
    },
    {
      icon: <TestTube className="w-8 h-8 text-chemical-600" />,
      title: 'Global Standards',
      description: 'Products manufactured to meet global quality and safety standards.'
    },
    {
      icon: <Award className="w-8 h-8 text-chemical-600" />,
      title: 'Certificate of Analysis',
      description: 'Detailed COA available for all products with comprehensive specifications.'
    }
  ];

  const productCategories = [
    {
      title: 'Liquid Bromine',
      description: 'High-purity liquid bromine for industrial applications including agro chemicals, pharmaceuticals, and fire retardants.',
      image: '/img/product/Bromine Bottle.jpg',
      link: '/products#bromine'
    },
    {
      title: 'Magnesium Chloride',
      description: 'High-purity magnesium chloride flakes for dust control, de-icing, food processing, and agriculture.',
      image: '/img/product/Magnesium Chloride Flakes.jpg',
      link: '/products#magnesium-chloride'
    }
  ];

  const testimonials = [
    {
      name: 'Deval Parekh',
      text: 'They are very prompt in their work and maintain good customer relationships.',
      rating: 5
    },
    {
      name: 'Mukesh Mishra',
      text: 'Good work with professional guidance from their employees.',
      rating: 5
    },
    {
      name: 'Tejas Sharma',
      text: 'They have wide variety of industrial chemicals to choose from, and their service is prompt staff.',
      rating: 5
    },
    {
      name: 'Rajesh Patel',
      text: 'Excellent quality products and outstanding customer service. Highly recommended!',
      rating: 5
    },
    {
      name: 'Priya Shah',
      text: 'Professional team with deep knowledge of chemical products. Great experience working with them.',
      rating: 5
    },
    {
      name: 'Amit Kumar',
      text: 'Reliable supplier with consistent quality. Their technical support is exceptional.',
      rating: 5
    },
    {
      name: 'Sneha Desai',
      text: 'Best chemical solutions provider in the region. Very satisfied with their products.',
      rating: 5
    },
    {
      name: 'Vikram Singh',
      text: 'Prompt delivery and excellent product quality. Will definitely work with them again.',
      rating: 5
    },
    {
      name: 'Anjali Mehta',
      text: 'Outstanding service and high-quality products. Their team is very professional.',
      rating: 5
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prevIndex) => 
        (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div>
      {/* Premium Quality Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Premium Quality */}
            <div className="text-left">
              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4">
                Premium <span className="text-chemical-600 dark:text-chemical-400">Quality</span>
              </h2>
              <p className="text-xl md:text-3xl text-chemical-600 dark:text-chemical-400 mb-8 font-bold tracking-wide">
                Trusted by industries nationwide
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {premiumImages.map((image, index) => (
                  <div 
                    key={index} 
                    className={`aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 transform hover:scale-105 animate-tilt ${
                      index === 9 ? 'lg:hidden' : ''
                    }`}
                    style={{
                      animationDelay: `${index * 0.1}s`,
                      animationDuration: '1s',
                      animationIterationCount: 'infinite',
                      animationTimingFunction: 'ease-in-out'
                    }}
                  >
                    <img 
                      src={image} 
                      alt={`Premium quality product ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                      loading={index < 4 ? 'eager' : 'lazy'}
                      decoding="async"
                      width={400}
                      height={400}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Company Description */}
            <div className="text-left">
              <div className="mb-6">
                <div className="w-20 h-20 bg-chemical-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                  <Beaker className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white text-center">
                  We are <span className="text-chemical-600 dark:text-chemical-400">Tatvam Chemicals</span>
                </h3>
              </div>
              <p className="text-2xl md:text-2xl text-chemical-600 dark:text-chemical-400 mb-8 font-bold tracking-wide text-center">
                Get the Perfect Industrial Chemicals here...
              </p>
              <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-10 leading-relaxed font-medium text-center">
                We have developed solutions specific to the needs of the Chemical Industry based on years of experience with customers in this industry.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/products#gallery"
                  className="bg-chemical-600 text-white hover:bg-chemical-700 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center leading-none"
                >
                  View Product Gallery
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a
                  href="/contact"
                  className="border-2 border-chemical-600 text-chemical-600 hover:bg-chemical-600 hover:text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center leading-none"
                >
                  Enquire Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-white dark:bg-gray-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              About Us
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
              We, Tatvam Chemicals at Halvad in Surendranagar, Gujarat, are leading manufacturers of chemical intermediates. 
              We offer a variety of chemical products. We are engaged in manufacturing, supplying and exporting a comprehensive 
              range of special chemicals, textile chemicals, paint & paper chemicals.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8">
              <div className="flex items-center mb-4">
                <CheckCircle className="w-6 h-6 text-chemical-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Who are we?</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Tatvam Chemicals is a manufacturer, formulator and co-packer of high quality specialty products.
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8">
              <div className="flex items-center mb-4">
                <Star className="w-6 h-6 text-chemical-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Why we?</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Our goal at Tatvam Chemicals is to provide high quality formulations and improve existing products 
                by using the latest formulation techniques.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Vision & Mission
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-700 rounded-xl p-8 shadow-lg">
              <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                To provide integral products and exceptional service to the chemical industry while sustaining a stable 
                and consistent reputation through innovative intelligence. Through adaptive improvement, we will continue 
                to be a leader in the manufacturing of speciality chemicals. An Indian company, we abide by two values 
                which include the purity of our products and the customer experience which has made this journey, one 
                filled with accomplishments and thriving.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white dark:bg-gray-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose Tatvam Chemicals?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              We deliver excellence in every product, backed by decades of experience and commitment to quality.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-chemical-50 dark:hover:bg-chemical-900/20 transition-colors duration-200">
                <div className="flex justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Premium Products
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Choose Best Products for Business and get Perfect Solutions
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {productCategories.map((category, index) => (
              <div key={index} className="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 bg-gradient-to-br from-chemical-100 to-blue-100 dark:from-chemical-900 dark:to-blue-900 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-chemical-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Beaker className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {category.title}
                    </h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {category.description}
                  </p>
                  <Link 
                    to={category.link}
                    className="inline-flex items-center text-chemical-600 dark:text-chemical-400 font-semibold hover:text-chemical-700 dark:hover:text-chemical-300 transition-colors duration-200 leading-none"
                  >
                    Explore Now!
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800 overflow-hidden">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Trusted by industries nationwide for quality and reliability
            </p>
          </div>
          
          <div className="relative">
            <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}>
              {testimonials.map((testimonial, index) => (
                <div key={index} className="w-full flex-shrink-0 px-4">
                  <div className="bg-white dark:bg-gray-700 rounded-xl p-8 shadow-lg max-w-2xl mx-auto">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-chemical-600 rounded-full flex items-center justify-center mr-4">
                        <span className="text-white font-semibold text-lg">
                          {testimonial.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {testimonial.name}
                        </h4>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          Satisfied Customer
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Navigation Dots */}
            <div className="flex justify-center mt-8 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial 
                      ? 'bg-chemical-600 w-8' 
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>
            
            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentTestimonial(prev => prev === 0 ? testimonials.length - 1 : prev - 1)}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white dark:bg-gray-700 rounded-full p-2 shadow-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setCurrentTestimonial(prev => prev === testimonials.length - 1 ? 0 : prev + 1)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white dark:bg-gray-700 rounded-full p-2 shadow-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Company Information
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Your trusted chemical solutions partner
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center border-l-4 border-chemical-600">
              <div className="w-12 h-12 bg-chemical-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Company Name</h3>
              <p className="text-gray-600 dark:text-gray-300">Tatvam Chemicals LLP</p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center border-l-4 border-chemical-600">
              <div className="w-12 h-12 bg-chemical-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">GST Number</h3>
              <p className="text-gray-600 dark:text-gray-300">24AARFT6743L1ZH</p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center border-l-4 border-chemical-600">
              <div className="w-12 h-12 bg-chemical-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Address</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                TATVAM CHEMICALS LLP, Halvad Road, Nava Ghanshyam Gadh, Halvad, Dist. Morbi, Gujarat (India) - 363351
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center border-l-4 border-chemical-600">
              <div className="w-12 h-12 bg-chemical-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Contact Numbers</h3>
              <p className="text-gray-600 dark:text-gray-300">
                <a href="tel:+919979953700" className="hover:text-chemical-600 dark:hover:text-chemical-400 transition-colors">Mo. 99799 53700 - Parthbhai</a>, 
                <a href="tel:+919825118564" className="hover:text-chemical-600 dark:hover:text-chemical-400 transition-colors">98251 18564 - Akshaybhai</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Link Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Our Location
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Visit our manufacturing facility in Gujarat, India
          </p>
          <a
            href="/contact#map"
            className="inline-flex items-center bg-chemical-600 text-white px-8 py-4 rounded-lg hover:bg-chemical-700 transition-colors duration-200 font-semibold"
          >
            <MapPin className="w-5 h-5 mr-2" />
            View Our Location
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-chemical-600">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-chemical-100 mb-8 max-w-2xl mx-auto">
            Contact us today for expert advice and customized solutions for your specific chemical needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-chemical-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center leading-none">
              Contact Us
            </Link>
            <Link to="/products" className="border-2 border-white text-white hover:bg-white hover:text-chemical-600 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center leading-none">
              View Products
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes shine {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-shine {
          animation: shine 2s infinite;
        }
        
        .shadow-3xl {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </div>
  );
};

export default Home;
